import { useCallback } from 'react';
import API from '../Api/api';
import { resolveSearchRadiusKm, positiveKm } from '../utils/nearLocationConfig';

/**
 * Fetch nearby properties with optional expanded radius fallback.
 */
export function useNearbyPropertyFetch() {
  const fetchNearby = useCallback(async ({
    lat,
    lng,
    radiusKm,
    page = 1,
    limit = 12,
    cityId = null,
    append = false,
    previous = [],
  }) => {
    const params = new URLSearchParams({
      lat: String(lat),
      lng: String(lng),
      radius_km: String(radiusKm),
      limit: String(limit),
      page: String(page),
    });
    if (cityId) params.set('city_id', String(cityId));

    const res = await API.get(`/property/nearby?${params}`);
    const data = Array.isArray(res.data?.data) ? res.data.data : [];
    return {
      data: append ? [...previous, ...data] : data,
      total: res.data?.total || 0,
      page,
      fallbackCity: res.data?.fallback_city || null,
      radiusKm: res.data?.radius_km ?? radiusKm,
    };
  }, []);

  const fetchDistrict = useCallback(async ({
    cityId,
    page = 1,
    limit = 12,
    append = false,
    previous = [],
  }) => {
    const res = await API.get(
      `/property/district?city_id=${cityId}&limit=${limit}&page=${page}`,
    );
    const data = Array.isArray(res.data?.data) ? res.data.data : [];
    return {
      data: append ? [...previous, ...data] : data,
      total: res.data?.total || 0,
      page,
      city: res.data?.city || null,
    };
  }, []);

  const fetchWithFallback = useCallback(async ({
    lat,
    lng,
    config,
    preferenceCityId,
    selectedCityName,
    page = 1,
    limit,
    append = false,
    previous = [],
  }) => {
    const cap = positiveKm(config.nearby_distance_km, 10000);
    const radius = resolveSearchRadiusKm(config);
    const result = await fetchNearby({
      lat,
      lng,
      radiusKm: radius,
      page,
      limit: limit || config.results_per_page || 12,
      cityId: preferenceCityId,
      append,
      previous,
    });

    if (result.data.length > 0 || append) {
      return { ...result, mode: 'nearby', radiusUsed: radius };
    }

    const behavior = config.fallback_behavior || 'district';

    if (behavior === 'expand_radius') {
      const expanded = Math.min(
        positiveKm(config.fallback_expand_radius_km, radius * 2),
        cap,
      );
      if (expanded > radius) {
        const expandedResult = await fetchNearby({
          lat,
          lng,
          radiusKm: expanded,
          page: 1,
          limit: limit || config.results_per_page || 12,
          cityId: preferenceCityId,
        });
        if (expandedResult.data.length > 0) {
          return { ...expandedResult, mode: 'nearby', radiusUsed: expanded, expanded: true };
        }
      }
    }

    const cityId = preferenceCityId || result.fallbackCity?.id;
    const cityName = selectedCityName || result.fallbackCity?.city_name || 'Your District';

    return {
      ...result,
      mode: 'empty',
      behavior,
      fallbackCityId: cityId,
      districtName: cityName,
      radiusUsed: radius,
    };
  }, [fetchNearby]);

  return { fetchNearby, fetchDistrict, fetchWithFallback };
}

export default useNearbyPropertyFetch;
